import './PackageItemForm.scss';

import { useForm } from '@refinedev/antd';
import { useResourceParams } from '@refinedev/core';
import { Button, Form, Input, Modal } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { RETURN_ITEM_TYPE } from '@/model/Logistic/Reception/ReturnItemType';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { RESOURCES } from '@/Resources';

import ReceptionPackageItemForm from './ReceptionPackageItemForm';
import ShipmentPackageItemForm from './ShipmentPackageItemForm';

interface PackageItemFormProps {
  packageItems: PackageItem[];
  setPackageItems: Function;
  closeModal: () => void;
  editingPackageItem?: PackageItem;
  canSellItems?: boolean;
  movilLink?: string;
}

const PackageItemForm = (props: PackageItemFormProps) => {
  const { t } = useTranslation();
  const { resource, action } = useResourceParams();
  const showShipmentData = useMemo(() => resource?.name == RESOURCES.SHIPMENTS, [resource]);
  const showReceptionData = useMemo(() => resource?.name == RESOURCES.RECEPTIONS, [resource]);
  const createMode = action === 'create';
  const { packageItems, setPackageItems, closeModal, editingPackageItem, canSellItems } = props;
  const { formProps } = useForm<PackageItem>({
    resource: 'packages_items',
    id: editingPackageItem?.id,
    action: 'edit',
    queryOptions: {
      enabled: false,
    },
    successNotification: {
      message: t('Item editado con éxito'),
      type: 'success',
    },
  });
  const movilLink = useMemo(
    () =>
      editingPackageItem && typeof editingPackageItem.package != 'number'
        ? (editingPackageItem.package?.movil_link ?? props.movilLink)
        : (props.movilLink ?? ''),
    [editingPackageItem, props.movilLink],
  );
  const closeAndReset = useCallback(
    (addMore?: boolean) => {
      if (!addMore) closeModal?.();
      formProps?.form?.resetFields();
    },
    [closeModal, formProps?.form],
  );
  const onFinish = useCallback(
    (addMore?: boolean) => {
      const values = formProps?.form?.getFieldsValue() as PackageItem;
      const newPackageItem: PackageItem = {
        ...values,
        name: formProps?.form?.getFieldValue('name'),
      };

      if (showReceptionData) {
        newPackageItem.is_returned = editingPackageItem?.is_returned ? true : false;
        newPackageItem.return_type = editingPackageItem?.return_type
          ? editingPackageItem.return_type
          : RETURN_ITEM_TYPE.SOLICITUD_REPARACION;
        if (!newPackageItem.in_warranty_period) {
          newPackageItem.warranty_start_date = undefined;
        }
      }

      if (
        showShipmentData &&
        newPackageItem.item_shipped_reference != SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE &&
        newPackageItem.item_shipped_reference != SHIPPING_ITEM_REFERENCE.PREVENTIVO
      ) {
        newPackageItem.in_warranty_period = false;
        newPackageItem.warranty_start_date = undefined;
      }

      const handleItemAddition = () => {
        setPackageItems([...packageItems, newPackageItem]);
        closeAndReset(addMore);
      };

      const handleItemUpdate = () => {
        if (editingPackageItem?.id) {
          newPackageItem.id = editingPackageItem.id;
          newPackageItem.toUpdate = true;
        }

        setPackageItems(
          packageItems.map(item => (item === editingPackageItem ? newPackageItem : item)),
        );
        closeAndReset(addMore);
      };

      const handleExistingItem = (itemWithSameProduct: PackageItem) => {
        if (createMode) {
          Modal.confirm({
            title: t('Elemento existente'),
            content: t('¿Desea reemplazar el elemento existente?'),
            onOk: () => {
              setPackageItems(
                packageItems.map(item => (item === itemWithSameProduct ? newPackageItem : item)),
              );
              closeAndReset(addMore);
            },
          });

          return;
        } else {
          Modal.error({
            title: t('Elemento existente'),
            content: t('Ya existe un item que contiene este elemento'),
          });

          return;
        }
      };

      const itemWithSameProduct = packageItems.find(item => {
        if (item !== editingPackageItem && !item.toDelete) {
          if (item.product_type == PRODUCT_TYPES.PRODUCT) {
            return (
              item.product === newPackageItem.product &&
              item.item_shipped_reference === newPackageItem.item_shipped_reference
            );
          } else if (item.product_type == PRODUCT_TYPES.UNIT) {
            return (
              item.unidad != undefined &&
              newPackageItem.unidad != undefined &&
              item.unidad === newPackageItem.unidad &&
              item.item_shipped_reference === newPackageItem.item_shipped_reference
            );
          } else if (item.product_type == PRODUCT_TYPES.DEVICE) {
            return (
              item.device != undefined &&
              newPackageItem.device != undefined &&
              item.device === newPackageItem.device &&
              item.item_shipped_reference === newPackageItem.item_shipped_reference
            );
          }
        }
      });

      if (editingPackageItem) {
        const hasChanges = Object.keys(newPackageItem).some(
          key =>
            editingPackageItem[key as keyof PackageItem] !==
            newPackageItem[key as keyof PackageItem],
        );

        if (!hasChanges) {
          closeAndReset();

          return;
        }

        if (itemWithSameProduct) {
          Modal.error({
            title: t('Elemento existente'),
            content: t('Ya existe un item que contiene este elemento'),
          });

          return;
        }

        handleItemUpdate();
      } else if (itemWithSameProduct) {
        handleExistingItem(itemWithSameProduct);
      } else {
        handleItemAddition();
      }
    },
    [
      formProps?.form,
      showReceptionData,
      showShipmentData,
      packageItems,
      editingPackageItem,
      setPackageItems,
      closeAndReset,
      createMode,
      t,
    ],
  );

  return (
    <Form
      {...formProps}
      initialValues={editingPackageItem}
      className="PackageItemForm"
      onFinish={() => onFinish()}>
      <div className="PackageItemFormContent">
        {showShipmentData && (
          <ShipmentPackageItemForm
            formProps={formProps}
            editingPackageItem={editingPackageItem}
            canSellItems={canSellItems}
            movilLink={movilLink}
            createMode={createMode}
          />
        )}
        {showReceptionData && (
          <ReceptionPackageItemForm
            formProps={formProps}
            editingPackageItem={editingPackageItem}
            movilLink={movilLink}
            createMode={createMode}
          />
        )}

        <Form.Item label={t('Observaciones')} name="comment">
          <Input placeholder={t('Observaciones')} />
        </Form.Item>
      </div>
      <div className="PackageItemFormButtons">
        <Button onClick={closeModal}>{t('Cancelar')}</Button>
        <Button type={'primary'} htmlType="submit">
          {editingPackageItem ? t('Actualizar') : t('Guardar')}
        </Button>
        {!editingPackageItem && (
          <Button
            type="primary"
            onClick={() => {
              void formProps.form?.validateFields().then(() => onFinish(true));
            }}>
            {t('Guardar y añadir otro')}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default PackageItemForm;
